import { TenantLeaseTypesEnum } from "@propertelligent/client-api";
import { useError } from "../../../utils/context/ErrorContext";
import { GetImages } from "../../../utils/GetImages";
import { Form } from "react-bootstrap";
import CustomInputs from "../../common/Inputs";
import { FORM_CONTROLFIELD_TYPES } from "../../../constants/constants";
import { FileUploader } from "react-drag-drop-files";
import RadioButton from "../../common/RadioButton";
import { TextCss } from "../../GlobalCss/TextCss";

const UploadLeaseAgreement = ({
  selectedOption,
  setSelectedOption,
  values,
  setValues,
  uploadAgreement,
  setUploadAgreement,
  agreementFileName,
  setAgreementFileName,
  isShowLeaseApplicationUploader = true,
  uploadAgreementError = false,
  setUploadAgreementError = (val: any) => { },
}) => {
  const { errors, setErrors, errorMessages, setErrorMessages, clearErrors } = useError();

  const fileTypes = ["PDF"];

  const handleRadioChange = (value) => {
    clearErrors();
    setSelectedOption(value);
    setErrors({
      ...errors,
      securityDeposit: false,
      rent: false,
      startDate: false,
      endDate: false,
    });
    if (value === TenantLeaseTypesEnum.MonthToMonth) {
      setValues((prev) => ({ ...prev, endDate: "" }));
    }
  };

  const handleStartDateChange = (e) => {
    setErrors({ ...errors, startDate: false });
    setValues({ ...values, startDate: e.target.value });
  };

  const handleEndDateChange = (e) => {
    setErrors({ ...errors, endDate: false });
    setValues({ ...values, endDate: e.target.value });
  };
  const handleUploadLeaseAgreement = (selectedFile: File | null) => {
    if (
      !selectedFile ||
      selectedFile.type !== "application/pdf"
    ) {
      setErrors((prevErrors) => ({ ...prevErrors, FileType: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        FileType: "Only PDF files are allowed",
      }));
      return;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      leaseAgreement: false,
      FileType: false,
      FileSize: false,
    }));
    setErrorMessages((prevMessages) => ({
      ...prevMessages,
      FileSize: "",
    }));
    setAgreementFileName(selectedFile.name);
    setUploadAgreement(selectedFile); // keep as File, preserves the name
    setUploadAgreementError(false);
  };

  // react-drag-drop-files' FileUploader enforces maxSize/types itself -- a file over maxSize
  // never reaches handleChange at all (it short-circuits and calls onSizeError instead), so
  // the manual size check above was dead code: oversized files were silently dropped with no
  // visible error. Wiring this callback is the only way to surface the library's own rejection
  // to the user. (The manual MIME-type check above is still reachable -- the library's own
  // type check is extension-based, so a .pdf-named file with a mismatched/empty MIME type can
  // still get through to handleChange.)
  const handleSizeError = () => {
    setErrors((prevErrors) => ({ ...prevErrors, FileType: false, FileSize: true }));
    setErrorMessages((prevMessages) => ({
      ...prevMessages,
      FileSize: "File size should not exceed 3MB",
    }));
  };

  const handleTypeError = () => {
    setErrors((prevErrors) => ({ ...prevErrors, FileType: true, FileSize: false }));
    setErrorMessages((prevMessages) => ({
      ...prevMessages,
      FileType: "Only PDF files are allowed",
    }));
  };

  const handleDeleteLeaseAgreement = () => {
    setUploadAgreementError(false);
    setErrors({
      ...errors,
      leaseAgreement: false,
      FileType: false,
      FileSize: false,
    });
    setErrorMessages((prevMessages) => ({
      ...prevMessages,
      FileSize: "",
    }));
    setAgreementFileName(null);
    setUploadAgreement(null);
  };

  const radioOptions = [
    {
      label: "Fix Lease",
      value: TenantLeaseTypesEnum.FixedTermLease,
      checked: selectedOption === TenantLeaseTypesEnum.FixedTermLease,
      onChange: () => handleRadioChange(TenantLeaseTypesEnum.FixedTermLease),
      name: "leaseType",
    },
    {
      label: "Month to Month",
      value: TenantLeaseTypesEnum.MonthToMonth,
      checked: selectedOption === TenantLeaseTypesEnum.MonthToMonth,
      onChange: () => handleRadioChange(TenantLeaseTypesEnum.MonthToMonth),
      name: "leaseType",
    },
  ];

  return (
    <>
      <p className={TextCss.black.sub_heading_600_14 + " mb-3"}>
        Select Lease Type <span className="red">*</span>
      </p>
      <div className="d-flex  gap-3">
        {radioOptions.map((option, index) => (
          <RadioButton
            label={option.label}
            value={option.value}
            checked={option.checked}
            onChange={option.onChange}
            name={option.name}
            mainClassName="d-flex align-items-center"
          />
        ))}
      </div>
      <>
        <div className="d-flex fixedComponentContainer mt-3">
          <div className="monthComponentItem">
            <div>
              <Form.Group controlId="formDate">
                <CustomInputs
                  name={"startDate"}
                  labelText="Start Date"
                  type={FORM_CONTROLFIELD_TYPES.DATE}
                  isFormControl
                  value={values.startDate || null}
                  onChange={(e) => handleStartDateChange(e)}
                  placeholder="Select a Date"
                  myClassName="containerTextField normal_text"
                  isError={errors.startDate}
                  errorMessages={errorMessages.startDate}
                  onBlur={() => {
                    if (values.startDate == "") {
                      setErrors({ ...errors, startDate: true });
                    } else {
                      setErrors({ ...errors, startDate: false });
                    }
                  }}
                  required
                />
              </Form.Group>
            </div>
          </div>
          {selectedOption == TenantLeaseTypesEnum.FixedTermLease ? (
            <div className="monthComponentItem">
              <div>
                <Form.Group controlId="formDate">
                  <CustomInputs
                    name={"endDate"}
                    labelText="End Date"
                    type={FORM_CONTROLFIELD_TYPES.DATE}
                    isFormControl
                    value={values.endDate || null}
                    onChange={(e) => handleEndDateChange(e)}
                    placeholder="Select a Date"
                    myClassName="containerTextField normal_text"
                    isError={errors.endDate}
                    errorMessages={errorMessages.endDate}
                    onBlur={() => {
                      if (values.endDate == "") {
                        setErrors({ ...errors, endDate: true });
                      } else {
                        setErrors({ ...errors, endDate: false });
                      }
                    }}
                    required
                  />
                </Form.Group>
              </div>
            </div>
          ) : null}
        </div>
        <div className="d-flex fixedComponentContainer">
          <div className="monthComponentItem">
            <div>
              <Form.Group controlId="formDate">
                <CustomInputs
                  name={"securityDeposit"}
                  labelText="Secuirty Deposit"
                  type={FORM_CONTROLFIELD_TYPES.INTEGER}
                  value={values.securityDeposit}
                  isFormControl
                  currencySymbol="$"
                  onChange={(e) => {
                    setValues({ ...values, securityDeposit: e.target.value });
                    setErrors({ ...errors, securityDeposit: false });
                  }}
                  placeholder="Enter Security Deposit"
                  myClassName="containerTextField normal_text"
                  isError={errors.securityDeposit}
                  errorMessages={errorMessages.securityDeposit}
                  height="46px"
                  onBlur={() => {
                    if (values.securityDeposit == "") {
                      setErrors({ ...errors, securityDeposit: true });
                    } else {
                      setErrors({ ...errors, securityDeposit: false });
                    }
                  }}
                  required
                  isCurrency={true}
                />
              </Form.Group>
            </div>
          </div>
          <div className="monthComponentItem">
            <CustomInputs
              name={"amount"}
              labelText="Rent"
              type={FORM_CONTROLFIELD_TYPES.INTEGER}
              value={values.amount}
              isFormControl
              currencySymbol="$"
              onChange={(e) => {
                setValues({ ...values, amount: e.target.value });
                setErrors({ ...errors, rent: false });
                setErrorMessages({ ...errorMessages, rent: "" });
              }}
              placeholder="Enter Rent"
              myClassName="containerTextField normal_text"
              isError={errors.rent}
              errorMessages={errorMessages.rent}
              height="46px"
              onBlur={() => {
                if (values.amount == "") {
                  setErrors({ ...errors, rent: true });
                  setErrorMessages({
                    ...errorMessages,
                    rent: "Rent is required",
                  });
                } else {
                  setErrors({ ...errors, rent: false });
                  setErrorMessages({ ...errorMessages, rent: "" });
                }
              }}
              required
              isCurrency={true}
            />
          </div>
        </div>
      </>

      <div className="TenantDocumentUploadContainer">
        <div
          className="d-flex flex-column justify-content-center"
          style={{ width: !isShowLeaseApplicationUploader ? "100%" : "50%" }}
        >
          <FileUploader
            handleChange={handleUploadLeaseAgreement}
            onSizeError={handleSizeError}
            onTypeError={handleTypeError}
            classes="TenantDocumentUploader"
            maxSize={3}
            types={fileTypes}
            children={
              <>
                <div className="fileUploaderTenant">
                  <img src={GetImages.upload} className="color-white-icon" />
                  <p className="normal_text fw-600 black text-center">
                    Upload Lease Agreement
                  </p>
                  <p className="sub_text grey_subtext1 text-center">
                    Max. size 3MB. .pdf
                  </p>
                </div>
              </>
            }
          />

          {uploadAgreementError && (
            <div style={{ marginTop: "8px" }}>
              <p className="errorMessageStyles">
                {errorMessages.leaseAgreement}
              </p>
            </div>
          )}
        </div>
      </div>
      {(errors.FileType || errors.FileSize) && (
        <div style={{ margin: "8px 0px" }}>
          {errors.FileType && (
            <p className="errorMessageStyles">{errorMessages.FileType}</p>
          )}
          {errors.FileSize && (
            <p className="errorMessageStyles">{errorMessages.FileSize}</p>
          )}
        </div>
      )}

      <div className="d-flex gap-3">
        {uploadAgreement && (
          <div
            className="documentPreview"
            title={agreementFileName || "Lease Agreement"}
          >
            <img width={21} height={24} src={GetImages.pdfImage} />
            <p className="normal_text grey_subtext1 fileName">
              {agreementFileName ? agreementFileName : "Lease Agreement"}
            </p>
            <img
              onClick={handleDeleteLeaseAgreement}
              className="clickable"
              width={30}
              height={30}
              src={GetImages.DeleteRedBg}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default UploadLeaseAgreement;
